import { type ReactNode, useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/utils';

/**
 * Clip a sliding label to its row while the title is entering or leaving.
 */
export const slideFadeRowClass = 'has-data-slide:overflow-hidden';

function reducedMotion(): boolean {
  return (
    typeof matchMedia === 'function' &&
    matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * Expanded activity-bar title. It is the flex item after the fixed icon, and
 * it takes only the remaining row width so a long label ellipsizes. The icon
 * stays the first shrink-0 child; showing or hiding this title does not move
 * it. `text-left` overrides the button UA `text-align: center`.
 */
export function RailExpandedTitle({
  shown,
  className,
  children,
}: {
  shown: boolean;
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <SlideFade
      shown={shown}
      className={cn(
        'pointer-events-none flex min-w-0 items-center overflow-hidden text-sm font-normal leading-5',
        // A leaving title stays mounted for the fade. Width 0 keeps it from
        // reserving a slot beside the icon.
        shown ? 'flex-1' : 'w-0 flex-none',
        className,
      )}
    >
      <span className="block w-full min-w-0 truncate text-left">
        {children}
      </span>
    </SlideFade>
  );
}

/**
 * Telegram `slideFade` (telegram-tt `Transition.scss`): enter from +1.5rem,
 * exit toward -1.5rem, both with opacity. The first paint is not animated.
 * Reduced motion mounts and unmounts with no animation.
 */
export function SlideFade({
  shown,
  className,
  children,
}: {
  shown: boolean;
  className?: string;
  children: ReactNode;
}): ReactNode {
  const [present, setPresent] = useState(shown);
  const [phase, setPhase] = useState<'idle' | 'enter' | 'exit'>('idle');
  const skipFirst = useRef(true);

  useEffect(() => {
    if (skipFirst.current) {
      skipFirst.current = false;
      setPresent(shown);
      setPhase('idle');
      return;
    }
    if (reducedMotion()) {
      setPresent(shown);
      setPhase('idle');
      return;
    }
    if (shown) {
      setPresent(true);
      setPhase('enter');
      return;
    }
    setPhase('exit');
  }, [shown]);

  if (!present) {
    return null;
  }

  return (
    <span
      data-slide={phase === 'idle' ? undefined : phase}
      className={cn(
        phase === 'enter' && 'app-slide-fade-in',
        phase === 'exit' && 'app-slide-fade-out',
        className,
      )}
      onAnimationEnd={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }
        if (phase === 'exit') {
          setPresent(false);
          setPhase('idle');
          return;
        }
        if (phase === 'enter') {
          setPhase('idle');
        }
      }}
    >
      {children}
    </span>
  );
}
